import {
  BackrsFormField,
  BackrsFormValues,
  BackrsUser,
  CareerCategory,
} from '@backrs_ui/common-ui/dist';
import {
  usePostProfileOptionAnswerMutation,
  usePostProfileOptionMutation,
  usePostUserMutation,
} from '@backrs_ui/grow-ui/src/redux/onboarding/onboarding.api';
import { useTrackUserActivityMutation } from '@backrs_ui/grow-ui/src/redux/user/user.api';

import { OnboardingQuestion } from '../onboarding.types';
import { extractUserDataFromForm } from '../utils/extractUserDataFields';
import {
  formatProfileOption,
  getProfileOptionKeysByQuestionId,
} from '../utils/formatFormQuestions';

type UsePostDataProps = Readonly<{
  questions?: Record<string, OnboardingQuestion>;
  careerPathCategories: CareerCategory[];
}>;

export const usePostData = ({ questions, careerPathCategories }: UsePostDataProps) => {
  const [postUser] = usePostUserMutation();
  const [postProfileOptionAnswer] = usePostProfileOptionAnswerMutation();
  const [postProfileOption] = usePostProfileOptionMutation();
  const [postUserActivity] = useTrackUserActivityMutation();

  const handleCreateUser = async (formData: BackrsFormValues): Promise<BackrsUser> => {
    const userData = extractUserDataFromForm(formData);

    const { user } = await postUser({
      userData: userData as BackrsFormValues,
    }).unwrap();

    return user;
  };

  const handlePostProfileOptionAnswers = async (formData: BackrsFormValues, user: BackrsUser) => {
    const profileOptionKeysByQuestionId = getProfileOptionKeysByQuestionId({
      formData,
      userId: user.id,
      questions,
      careerPathCategories,
    });

    for (const [questionId, profileOptionAnswers] of Object.entries(
      profileOptionKeysByQuestionId,
    )) {
      await postProfileOptionAnswer({
        profileOptionAnswers,
        access_token: user.access_token,
        postKey: questionId,
      }).unwrap();
    }
  };

  // now we can update the user activity endpoint
  const handlePostUserActivity = async (user: BackrsUser) => {
    await postUserActivity(user.access_token).unwrap();
  };

  /* this function is used to post to the new user profile update endpoint. Long term all profile options will be posted here */
  const handlePostProfileOption = async (
    formData: BackrsFormValues,
    user: BackrsUser,
    questions?: Record<string, OnboardingQuestion>,
  ) => {
    if (!questions) {
      return;
    }

    const profileOptionQuestions: BackrsFormField[] = formatProfileOption({ questions }) || [];

    const onlyFilledProfileOptions = profileOptionQuestions?.filter(Boolean);

    if (!onlyFilledProfileOptions) {
      return;
    }

    for (const userFormData of Object.keys(formData)) {
      const profileOption = onlyFilledProfileOptions.find((option) => option.name === userFormData);

      if (!profileOption) continue;

      const objectToPost = {
        [userFormData]: formData[userFormData],
      };

      await postProfileOption({
        profileOptionAnswers: objectToPost,
        access_token: user.access_token,
        postKey: userFormData,
      }).unwrap();
    }
  };

  return {
    handleCreateUser,
    handlePostProfileOptionAnswers,
    handlePostUserActivity,
    handlePostProfileOption,
  };
};
